import type { AssignedSubTab } from "./assigned-sub-tab"
import type { InboxState } from "@/lib/states/global-state"
import {
	resolveEffectiveInboxTab,
	resolveInboxStateScope,
} from "./assigned-sub-tab"

interface MembershipThread {
	state?: string | null
	assigneeId?: string | null
	replied?: boolean | null
	lastOutboundMessageAt?: number | null
	slaDeadlineAt?: number | null
}

function matchesStateScope(
	thread: MembershipThread,
	scope: AssignedSubTab,
): boolean {
	const state = thread.state ?? null
	if (scope === "snoozed") return state === "SNOOZED"
	if (scope === "done") return state === "PROCESSED"
	if (scope === "close-loop") return state === "COMPLETED"
	return state !== "SNOOZED" && state !== "PROCESSED" && state !== "COMPLETED"
}

export function threadInTab(
	thread: MembershipThread,
	tab: InboxState,
	userId: string | undefined,
	nowMs: number = Date.now(),
): boolean {
	const state = thread.state ?? null
	const assigneeId = thread.assigneeId ?? null
	const replied = thread.replied ?? false
	const isProcessed = state === "PROCESSED"
	const isSnoozed = state === "SNOOZED"
	const isCompleted = state === "COMPLETED"

	switch (tab) {
		case "snoozed":
			return isSnoozed
		case "done":
			return isProcessed
		case "open":
			return !isProcessed && !isSnoozed && !isCompleted
		case "close-loop":
			return isCompleted
		// Close-loop threads assigned to me stay in My inbox; All still skips them.
		case "my":
			return !!userId && assigneeId === userId && !isProcessed && !isSnoozed
		case "my-snoozed":
			return !!userId && assigneeId === userId && isSnoozed
		case "my-done":
			return !!userId && assigneeId === userId && isProcessed
		case "my-close-loop":
			return !!userId && assigneeId === userId && isCompleted
		case "new":
			return (state === "NEW" || state === "UNSNOOZED") && !assigneeId
		case "needs-response":
			return !replied && !isProcessed && !isSnoozed && !isCompleted
		case "sla-breached":
			return (
				!isProcessed &&
				!isSnoozed &&
				!isCompleted &&
				!!thread.slaDeadlineAt &&
				thread.slaDeadlineAt > 0 &&
				thread.slaDeadlineAt < nowMs
			)
		// A view's own filters are not on the thread row, so membership can only
		// be decided by the list query.
		case "custom-view":
			return true
		default:
			return false
	}
}

// Membership for the sidebar tab + All / Snoozed / Done sub-tab together.
export function threadInInboxView(
	thread: MembershipThread,
	inboxState: InboxState,
	subTab: AssignedSubTab,
	userId: string | undefined,
	hasActiveCustomView: boolean,
	nowMs: number = Date.now(),
	viewOwnsStatuses = false,
): boolean {
	const scope = resolveInboxStateScope(
		inboxState,
		hasActiveCustomView,
		subTab,
		viewOwnsStatuses,
	)
	if (scope) {
		if (!matchesStateScope(thread, scope)) return false
		if (inboxState === "new") {
			if (thread.assigneeId) return false
			// Open sub-tab matches the list query (NEW / UNSNOOZED only).
			if (scope === "open") {
				return thread.state === "NEW" || thread.state === "UNSNOOZED"
			}
			return true
		}
		if (inboxState === "needs-response") return thread.replied === false
		if (inboxState === "sla-breached") {
			return (
				thread.replied === false &&
				!!thread.slaDeadlineAt &&
				thread.slaDeadlineAt > 0 &&
				thread.slaDeadlineAt < nowMs
			)
		}
		if (inboxState === "custom-view") return true
	}
	const effective = resolveEffectiveInboxTab(inboxState, subTab)
	return threadInTab(thread, effective, userId, nowMs)
}

// Pick the most relevant sidebar tab that contains this thread. Snoozed /
// done live under Open (or Assigned to me) via the All / Snoozed / Done
// sub-tab, so they never return as a sidebar tab of their own.
export function inferThreadTab(
	thread: MembershipThread,
	userId: string | undefined,
): InboxState {
	if (thread.state === "COMPLETED") return "close-loop"
	if (userId && thread.assigneeId === userId) return "my"
	if (thread.state === "PROCESSED" || thread.state === "SNOOZED") return "open"
	if (
		(thread.state === "NEW" || thread.state === "UNSNOOZED") &&
		!thread.assigneeId
	) {
		return "new"
	}
	return "open"
}

export function inferThreadSubTab(thread: MembershipThread): AssignedSubTab {
	if (thread.state === "PROCESSED") return "done"
	if (thread.state === "SNOOZED") return "snoozed"
	if (thread.state === "COMPLETED") return "close-loop"
	return "open"
}
